import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faSparkles, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";

interface SelectedSignalChipProps {
	/** The signal summary/title shown as the chip label. */
	description: string;
	/** Removes the signal so the generation runs without any trigger. */
	onRemove: () => void;
	disabled?: boolean;
}

/**
 * The signal currently seeded into a single-lead generation, shown as a removable chip below the
 * "Tell AI what to write" input. Removing it clears `seedSignalId` and flags `ignoreSignal` so the
 * copy is written without considering any signal (PREC-3710). Hand-rolled in Tailwind — the shared
 * `Chip` is MUI-based and capitalizes its label, which mangles a signal sentence.
 */
export const SelectedSignalChip: FunctionComponent<SelectedSignalChipProps> = ({
	description,
	onRemove,
	disabled,
}) => {
	const { t } = useTranslation();

	return (
		<div
			aria-label={t("messages.generateBar.signalChip.ariaLabel") || undefined}
			className="flex items-center gap-2 rounded-lg border border-border-light bg-brand-white px-3 py-2 text-xs"
			data-testid="selected-signal-chip"
		>
			<Icon className="text-brand-light shrink-0" icon={faSparkles} />
			<span className="font-semibold text-text shrink-0">
				{t("messages.generateBar.signalChip.label")}
			</span>
			<span className="min-w-0 flex-1 truncate text-text-meta" title={description}>
				{description}
			</span>
			<button
				aria-label={t("messages.generateBar.signalChip.remove") || undefined}
				className="shrink-0 cursor-pointer text-text-meta hover:text-status-error disabled:cursor-not-allowed disabled:opacity-50"
				disabled={disabled}
				title={t("messages.generateBar.signalChip.remove") || undefined}
				type="button"
				onClick={onRemove}
			>
				<Icon icon={faXmark} />
			</button>
		</div>
	);
};
